import type { ReactNode } from "react";
import type { TenantLabels } from "../../lib/tenantLabels";
import {
  CalendarIcon,
  DocumentIcon,
  LinkIcon,
  LocationPinIcon,
  MessageIcon,
  PersonIcon,
  SettingsIcon,
  VideoIcon
} from "../icons/AppIcons";
import type { OperatorBottomNavigationItem } from "./OperatorBottomNavigation";

type PageLink = {
  to: string;
  label: string;
};

function resolveLinkTarget(linkTo: string, tenantSlug: string): string {
  return linkTo.replace(":tenantSlug", tenantSlug);
}

function isGlobalDestination(path: string): boolean {
  return (
    /\/home$/.test(path)
    || /\/teacher$/.test(path)
    || /\/teacher\/admin$/.test(path)
    || /\/support$/.test(path)
  );
}

function resolveNavigationIcon(path: string): ReactNode {
  if (path.includes("/absence-request")) {
    return <CalendarIcon size={24} />;
  }

  if (path.includes("/recurring-slots")) {
    return <LocationPinIcon size={24} />;
  }

  if (path.includes("/students") || path.includes("/family-accounts") || path.includes("/guardian-link")) {
    return <PersonIcon size={24} />;
  }

  if (path.includes("/enrollments") || path.includes("/integrations")) {
    return <LinkIcon size={24} />;
  }

  if (path.includes("/lessons") || path.includes("/schedule") || path.includes("/events") || path.includes("/public-events")) {
    return <CalendarIcon size={24} />;
  }

  if (path.includes("/member-communications")) {
    return <MessageIcon size={24} />;
  }

  if (path.includes("/videos")) {
    return <VideoIcon size={24} />;
  }

  if (path.includes("/tenant") || path.includes("/public-settings")) {
    return <SettingsIcon size={24} />;
  }

  if (path.includes("/billing")) {
    return <DocumentIcon size={24} />;
  }

  return <DocumentIcon size={24} />;
}

function resolveActivePrefixes(path: string): string[] {
  if (path.includes("/absence-request")) {
    return [path.replace(/\/new$/, "")];
  }

  if (path.includes("/students")) {
    return [
      path,
      path.replace(/\/students$/, "/family-accounts")
    ];
  }

  if (path.includes("/guardian-links")) {
    return [path, path.replace(/\/guardian-links$/, "/guardian-link-requests")];
  }

  if (path.includes("/lessons")) {
    return [path];
  }

  if (path.includes("/schedule")) {
    return [
      path,
      path.replace(/\/schedule$/, "/schedule-months"),
      path.replace(/\/schedule$/, "/occurrences")
    ];
  }

  return [path];
}

export function resolveOperatorBottomItems(
  links: readonly PageLink[],
  tenantSlug: string,
  labels: TenantLabels
): OperatorBottomNavigationItem[] {
  const seen = new Set<string>();
  const items: OperatorBottomNavigationItem[] = [];

  for (const link of links) {
    const to = resolveLinkTarget(link.to, tenantSlug);

    if (isGlobalDestination(to) || seen.has(to)) {
      continue;
    }

    seen.add(to);

    items.push({
      key: to,
      label: link.label
        .replace("講師ホームへ戻る", `${labels.teacher}ホーム`)
        .replace("管理ホームへ戻る", "管理ホーム")
        .replace(/へ戻る$/, ""),
      to,
      icon: resolveNavigationIcon(to),
      match: "prefix",
      activePrefixes: resolveActivePrefixes(to)
    });
  }

  return items;
}
